import React, { FC, useEffect, useRef } from "react";
import { defineMessages, useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import { Button } from "@natera/form";
import { routes } from "@app/routing";
import { HEAP_EVENTS } from "@app/provider/types";
import { usePatientEducation } from "@app/provider";
import { usePatientEducationHeap } from "@app/hooks/usePatientEducationHeap";
import { PatientEducationLoader } from "./PatientEducationLoader";
import { Progress } from "@app/components";
import { useWistiaScript } from "@app/hooks/useWistiaScript";

import "./patientEducation.scss";

// Wistia type declarations (local to this file only)
interface WistiaVideo {
  bind: (eventName: string, callback: (value?: number) => void) => void;
  unbind: (eventName: string) => void;
  play: () => void;
  pause: () => void;
  duration: () => number;
  time: () => number;
  secondsWatchedVector: () => number[];
}

interface WistiaQueueItem {
  id: string;
  onReady: (video: WistiaVideo) => void;
}

const messages = defineMessages({
  panoramaTitle: {
    id: "patientEducation.video.panoramaTitle",
    defaultMessage: "Panorama Non-Invasive Prenatal Testing",
  },
  horizonTitle: {
    id: "patientEducation.video.horizonTitle",
    defaultMessage: "Horizon Carrier Screening",
  },
  panoramaStep: {
    id: "patientEducation.video.panoramaStep",
    defaultMessage: "Step 1 of 3",
  },
  horizonStep: {
    id: "patientEducation.video.horizonStep",
    defaultMessage: "Step 2 of 3",
  },
  nextButton: {
    id: "patientEducation.video.nextButton",
    defaultMessage: "Next",
  },
  backButton: {
    id: "patientEducation.video.backButton",
    defaultMessage: "Back",
  },
});

// Wistia video IDs based on type and language
const WISTIA_VIDEO_IDS = {
  panorama: {
    en: "rg9t8zvgsh",
    es: "28q5pdm080",
  },
  horizon: {
    en: "50ueave7jo",
    es: "r9jmrx8pqv",
  },
};

interface VideoPageProps {
  videoType: "panorama" | "horizon";
}

const VIDEO_CONFIG = {
  panorama: {
    step: 1,
    progress: 33.33,
    title: messages.panoramaTitle,
    stepIndicator: messages.panoramaStep,
    nextRoute: (clinicId: string) =>
      routes.patientEducationHorizonPage(clinicId),
    backRoute: (clinicId: string) => routes.patientEducationPage(clinicId),
    heapEvents: {
      play: HEAP_EVENTS.upp_prenataled_pano_video_play,
      progress25: HEAP_EVENTS.upp_prenataled_pano_video_25,
      progress50: HEAP_EVENTS.upp_prenataled_pano_video_50,
      progress75: HEAP_EVENTS.upp_prenataled_pano_video_75,
      progress100: HEAP_EVENTS.upp_prenataled_pano_video_100,
    },
  },
  horizon: {
    step: 2,
    progress: 66.66,
    title: messages.horizonTitle,
    stepIndicator: messages.horizonStep,
    nextRoute: (clinicId: string) =>
      routes.patientEducationQuestionnairePage(clinicId),
    backRoute: (clinicId: string) =>
      routes.patientEducationPanoramaPage(clinicId),
    heapEvents: {
      play: HEAP_EVENTS.upp_prenataled_horizon_video_play,
      progress25: HEAP_EVENTS.upp_prenataled_horizon_video_25,
      progress50: HEAP_EVENTS.upp_prenataled_horizon_video_50,
      progress75: HEAP_EVENTS.upp_prenataled_horizon_video_75,
      progress100: HEAP_EVENTS.upp_prenataled_horizon_video_100,
    },
  },
};

const VideoPage: FC<VideoPageProps> = ({ videoType }) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { publicClinicId } = usePatientEducation();
  const { trackEvent } = usePatientEducationHeap(publicClinicId);
  const videoContainerRef = useRef<HTMLDivElement>(null);

  useWistiaScript();

  const config = VIDEO_CONFIG[videoType];
  const storageKey = `pep_${videoType}Percent`;
  const language = intl.locale === "es" ? "es" : "en";
  const videoId = WISTIA_VIDEO_IDS[videoType][language];

  // Track which milestones have been fired to avoid duplicates
  const firedMilestonesRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    if (!videoId) return;

    // Reset fired milestones when video changes
    firedMilestonesRef.current = new Set();

    // Remove any existing video container content to force reload
    if (videoContainerRef.current) {
      // Clear existing content
      while (videoContainerRef.current.firstChild) {
        videoContainerRef.current.removeChild(
          videoContainerRef.current.firstChild
        );
      }

      // Create video embed div using DOM methods
      // Note: videoFoam=true is Wistia's syntax for responsive video sizing
      const videoDiv = document.createElement("div");
      videoDiv.className = `wistia_embed wistia_async_${videoId} videoFoam=true`;
      videoDiv.style.position = "relative";
      videoDiv.style.height = "100%";
      videoDiv.textContent = "\u00A0"; // &nbsp; as text content (safer than innerHTML)

      videoContainerRef.current.appendChild(videoDiv);
    }

    // Initialize video tracking
    const initVideo = () => {
      // Use type assertion to work with _wq as a generic array
      const wq = (window as unknown as { _wq?: WistiaQueueItem[] })._wq;
      if (wq) {
        wq.push({
          id: videoId,
          onReady: (video: WistiaVideo) => {
            // Track video play event
            video.bind("play", () => {
              trackEvent({ eventKey: config.heapEvents.play });
            });

            video.bind("timechange", () => {
              const watchedVector = video.secondsWatchedVector();

              if (watchedVector) {
                const watchedSeconds = watchedVector.filter(
                  (second) => second > 0
                ).length;
                const rawPercent = Math.ceil(
                  (watchedSeconds / watchedVector.length) * 100
                );
                const watchedPercent = Math.round(rawPercent / 5) * 5; // Round to nearest 5%
                localStorage.setItem(storageKey, watchedPercent.toString());

                // Track progress milestones (only fire once per milestone)
                const milestones = [
                  { threshold: 25, event: config.heapEvents.progress25 },
                  { threshold: 50, event: config.heapEvents.progress50 },
                  { threshold: 75, event: config.heapEvents.progress75 },
                  { threshold: 100, event: config.heapEvents.progress100 },
                ];

                for (const { threshold, event } of milestones) {
                  if (
                    rawPercent >= threshold &&
                    !firedMilestonesRef.current.has(threshold)
                  ) {
                    firedMilestonesRef.current.add(threshold);
                    trackEvent({ eventKey: event });
                  }
                }
              }
            });
          },
        });
      }
    };

    if ((window as unknown as { _wq?: WistiaQueueItem[] })._wq) {
      initVideo();
    } else {
      setTimeout(initVideo, 500);
    }
  }, [videoId, storageKey, config.heapEvents, trackEvent]);

  const handleNext = () => {
    if (!publicClinicId) return;

    // Track next button click
    const eventKey =
      videoType === "panorama"
        ? HEAP_EVENTS.upp_prenataled_pano_next
        : HEAP_EVENTS.upp_prenataled_horizon_next;
    trackEvent({ eventKey });

    navigate(config.nextRoute(publicClinicId));
  };

  const handleBack = () => {
    if (!publicClinicId) return;
    navigate(config.backRoute(publicClinicId));
  };

  return (
    <PatientEducationLoader>
      <div className="patient-education-video__container">
        <div className="patient-education-video__wrapper">
          <Progress
            className="patient-education-video__progress"
            value={config.progress}
          />
          <div className="patient-education-video__content">
            <h2 className="patient-education__step-number">
              {intl.formatMessage(config.stepIndicator)}
            </h2>

            <h1 className="patient-education__video-title">
              {intl.formatMessage(config.title)}
            </h1>

            <div className="patient-education__video" ref={videoContainerRef}>
              <div
                className={`wistia_embed wistia_async_${videoId} videoFoam=true`}
                style={{ position: "relative", height: "100%" }}
              >
                &nbsp;
              </div>
            </div>

            <div className="patient-education__video-actions">
              <Button onClick={handleBack}>
                {intl.formatMessage(messages.backButton)}
              </Button>
              <Button onClick={handleNext} unelevated>
                {intl.formatMessage(messages.nextButton)}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </PatientEducationLoader>
  );
};

export default VideoPage;
